import { type Theme, type WithTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Property } from 'csstype';
import { type ChangeEvent, forwardRef, type SubmitEvent, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Box from 'app/components/v2/components/Box';
import Icon from 'app/components/v2/components/Icon';
import IconButton from 'app/components/v2/components/IconButton';
import LoadingSpinner from 'app/components/v2/components/LoadingSpinner';
import { useObserveRefDimensions } from 'app/components/v2/hooks/useResizeObserver';
import { useTextField } from 'app/hooks';

import {
  globalSearchBorderRadiusPill,
  globalSearchInputCompactThreshold,
  globalSearchInputMaxWidth,
  globalSearchTransitionSpeedMs,
} from '../constants';

export interface GlobalSearchInputProps {
  isLoading: boolean;
  onClear: () => void;
  onSubmit: (searchValue: string) => void;
  placeholder: string;
}

interface Props extends GlobalSearchInputProps {
  onFocus: () => void;
}

const OuterContainer = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.semanticColors.global_search_background,
  borderColor: 'transparent',
  borderRadius: globalSearchBorderRadiusPill,
  borderStyle: 'solid',
  borderWidth: 4,
  display: 'flex',
  flex: 1,
  height: 42,
  justifyContent: 'space-between',
  maxWidth: globalSearchInputMaxWidth,
  minWidth: 0,
  overflow: 'hidden',
  paddingLeft: theme.spacing * 2,

  // these styles prevent the user agent (browser) from applying its own styles to the input when autofill is triggered
  /* eslint-disable @typescript-eslint/naming-convention */
  // eslint-disable-next-line sort-keys
  'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active': {
    WebkitBackgroundClip: 'text',
    WebkitBoxShadow: '0 0 0px 1000px #00000000 inset !important',
    WebkitTextFillColor: `${theme.semanticColors.global_search_text} !important`,
    /* eslint-enable @typescript-eslint/naming-convention */
    transition: 'background-color 9999s ease-out',
  },
}));

const StyledInput = styled.input(({ theme }) => ({
  background: 'unset',
  border: 'unset',
  caretColor: theme.semanticColors.global_search_text,
  color: theme.semanticColors.global_search_text,
  fontSize: 14,
  height: '100%',
  outline: 'unset',
  width: '100%',

  // eslint-disable-next-line sort-keys, @typescript-eslint/naming-convention
  '&::placeholder': {
    color: theme.semanticColors.global_search_placeholder,
    transition: `${globalSearchTransitionSpeedMs}ms`,
  },

  /* eslint-disable @typescript-eslint/naming-convention */
  '&:hover': {
    '&::placeholder': {
      /* eslint-enable @typescript-eslint/naming-convention */
      color: theme.semanticColors.global_search_text,
    },
  },
}));

const IconButtonContainer = styled(Box)(({ hasValue, theme }: WithTheme<{ hasValue: boolean }, Theme>) => ({
  opacity: hasValue ? 1 : 0,
  pointerEvents: (hasValue ? 'auto' : 'none') as Property.PointerEvents,
  transition: `${globalSearchTransitionSpeedMs}ms`,

  // eslint-disable-next-line sort-keys
  button: {
    cursor: hasValue ? 'pointer' : 'default',
    // eslint-disable-next-line sort-keys, @typescript-eslint/naming-convention
    '--button-content-color': theme.semanticColors.global_search_placeholder,

    // eslint-disable-next-line sort-keys, @typescript-eslint/naming-convention
    '&:hover': {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      '--button-content-color': theme.semanticColors.global_search_text,
    },
  },
}));

const ButtonContent = styled.span<{ isVisible: boolean }>(({ isVisible }) => ({
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'center',
  opacity: isVisible ? 1 : 0,
  position: 'absolute',
  transition: 'opacity 200ms ease-in-out',
}));

const StyledButton = styled.button<{ isCompact: boolean }>(({
  isCompact,
  theme,
}) => ({
  alignItems: 'center',
  backgroundColor: theme.semanticColors.nav_background,
  border: 'unset',
  borderRadius: globalSearchBorderRadiusPill,
  color: theme.semanticColors.global_search_text,
  display: 'inline-flex',
  fontSize: 14,
  fontWeight: 700,
  height: '100%',
  justifyContent: 'center',
  overflow: 'hidden',
  padding: '0 12px',
  position: 'relative',
  transition: [
    `width ${globalSearchTransitionSpeedMs}ms ease-in-out`,
    `background-color ${globalSearchTransitionSpeedMs}ms`,
    `color ${globalSearchTransitionSpeedMs}ms`,
  ].join(', '),
  width: isCompact ? 34 : 78,

  // eslint-disable-next-line sort-keys, @typescript-eslint/naming-convention
  '&:hover': {
    backgroundColor: theme.semanticColors.global_search_button_hover,
  },

  // eslint-disable-next-line sort-keys, @typescript-eslint/naming-convention
  '&:disabled': {
    backgroundColor: theme.semanticColors.global_search_button_disabled,
    color: theme.semanticColors.global_search_placeholder,
    cursor: 'not-allowed',

    path: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      '--icon-color': theme.semanticColors.global_search_placeholder,
    },
  },
}));

const GlobalSearchInput = forwardRef<HTMLFormElement, Props>(({
  isLoading,
  onClear,
  onFocus,
  onSubmit,
  placeholder,
}, ref) => {
  const searchField = useTextField();
  const containerRef = useRef<HTMLFormElement>(null);
  const { width } = useObserveRefDimensions(containerRef as React.RefObject<HTMLElement>);
  const showIcon = width < globalSearchInputCompactThreshold;

  const { t } = useTranslation('common');

  const handleClear = () => {
    searchField.clear();
    onClear();
  };

  const handleChange = (evt: ChangeEvent<HTMLInputElement>) => {
    const newValue = evt.target.value;
    searchField.onChange(evt);

    if (!newValue) {
      onClear();
    }
  };

  const handleSubmit = (evt: SubmitEvent<HTMLFormElement>) => {
    evt.preventDefault();
    onSubmit(searchField.value.trim());
  };

  const handleRef = useCallback((element: HTMLFormElement | null) => {
    // Handle both refs
    if (typeof ref === 'function') {
      ref(element);
    } else if (ref) {
      ref.current = element;
    }
    containerRef.current = element;
  }, [ref]);

  return (
    <OuterContainer
      as="form"
      onSubmit={handleSubmit}
      ref={handleRef}
    >
      <StyledInput
        id="global-search-input"
        onChange={handleChange}
        onFocus={onFocus}
        placeholder={placeholder}
        value={searchField.value}
      />

      <Box
        alignItems="center"
        display="flex"
        fullHeight
      >
        <IconButtonContainer hasValue={!!searchField.value}>
          <IconButton
            ariaLabel={t('globalSearch.input.clear', 'activate to clear search')}
            circle
            iconName="close"
            iconSize="xxs"
            onClick={handleClear}
            role="button"
          />
        </IconButtonContainer>

        <StyledButton
          data-testid="global-search-submit-button"
          disabled={!searchField.value}
          isCompact={showIcon || isLoading}
          type="submit"
        >
          <ButtonContent isVisible={isLoading}>
            <LoadingSpinner />
          </ButtonContent>
          <ButtonContent isVisible={!isLoading}>
            {showIcon
              ? (
                <Icon
                  color="inherit"
                  name="search"
                  size="xxs"
                />
              )
              : (
                t('search', 'Search')
              )}
          </ButtonContent>
        </StyledButton>
      </Box>
    </OuterContainer>
  );
});

export default GlobalSearchInput;
